import { Drawer, Skeleton } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import ArrowLeftIcon from '@untitled-ui/icons-react/build/esm/ArrowLeft';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { RouterLink } from 'src/components/router-link';
import { Seo } from 'src/components/seo';
import { useMounted } from 'src/hooks/use-mounted';
import { paths, permissions } from 'src/paths';
import { getSinglePatient, persistPatientIdAction } from 'src/redux/actions/patientAction';
import { getInitials } from 'src/utils/get-initials';
import deactiveIcon from '../../icons/untitled-ui/duocolor/deactive.svg';
import PatientInfo from './patient-info';
import PatientInfoDrawer from './patient-info-drawer';
import { customersApi } from './types';
import { Customer, CustomerInvoice, CustomerLog } from './types/customer';
import useFeatureToggle from 'src/hooks/use-feature-toggle';
import { mdsCommunicationTreatment } from 'src/config';
const {
  patientDetails,
  screening,
  episodeOfCare,
  patients,
  deactivate,
  mdsCommunication,
  evalMds,
  recertiMds,
  dischargeMds,
} = paths;
const { DEACTIVATE_PATIENT, ACTIVATE_PATIENT, READ_EPISODE_OF_CARE, READ_SCREENINGS } = permissions;
const tabs = [
  { label: 'Episode of Care', value: 'episode-care' },
  { label: 'Screening', value: 'screening' },
  { label: 'Info', value: 'details' },
  { label: 'MDS', value: 'mds' },
];

const useCustomer = (): Customer | null => {
  const isMounted = useMounted();
  const [customer, setCustomer] = useState<Customer | null>(null);

  const handleCustomerGet = useCallback(async () => {
    try {
      const response = await customersApi.getCustomer();

      if (isMounted()) {
        setCustomer(response);
      }
    } catch (err) {
      console.error(err);
    }
  }, [isMounted]);

  useEffect(
    () => {
      handleCustomerGet();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return customer;
};

const useInvoices = (): CustomerInvoice[] => {
  const isMounted = useMounted();
  const [invoices, setInvoices] = useState<CustomerInvoice[]>([]);

  const handleInvoicesGet = useCallback(async () => {
    try {
      const response = await customersApi.getInvoices();

      if (isMounted()) {
        setInvoices(response);
      }
    } catch (err) {
      console.error(err);
    }
  }, [isMounted]);

  useEffect(
    () => {
      handleInvoicesGet();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return invoices;
};

const useLogs = (): CustomerLog[] => {
  const isMounted = useMounted();
  const [logs, setLogs] = useState<CustomerLog[]>([]);

  const handleLogsGet = useCallback(async () => {
    try {
      const response = await customersApi.getLogs();

      if (isMounted()) {
        setLogs(response);
      }
    } catch (err) {
      console.error(err);
    }
  }, [isMounted]);

  useEffect(
    () => {
      handleLogsGet();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return logs;
};

const PatientDetails = () => {
  const location = useLocation();
  const customer = useCustomer();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const patientId = state?.patientId;
  const [popUpContent, setPopup] = useState(false);
  const anchorRef = useRef<any>(null);
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const getPatientId = useSelector((state: any) => state?.getPatientIdReducer);
  const patientDataLoader = useSelector((state: any) => state.getSinglePatientReducer);
  const permissionData = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.currentPermissions
  );
  const [isMdsOn] = useFeatureToggle(mdsCommunicationTreatment, 'ENTERPRISE');

  const [openPatientInfoDetails, setOpenPatientInfoDetails] = useState(false);

  const handleClosePatientInfoDrawer = () => {
    setOpenPatientInfoDetails(false);
  };
  const handleOpenPatientInfoDrawer = () => {
    setOpenPatientInfoDetails(true);
  };
  const handleClose = useCallback((event: Event): void => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setPopup(false);
  }, []);
  const handleAddPatientPopUp = useCallback((): void => {
    setPopup((prevOpen) => !prevOpen);
  }, []);

  useEffect(() => {
    dispatch(
      getSinglePatient({
        patientId: getPatientId,
      })
    );
  }, [getPatientId]);

  if (!customer) {
    return null;
  }

  const onDeactivatePatient = () => {
    navigate(`${patients}/${deactivate}`, {
      state: { value: !patientData?.active },
    });
  };

  const redirectToTab = (value: string) => {
    switch (value) {
      case 'details':
        navigate(`${patients}/${patientDetails}`, {
          state: { patientId: getPatientId },
        });
        break;
      case 'screening':
        navigate(`${patients}/${patientDetails}${screening}`, {
          state: { patientId: getPatientId },
        });
        break;
      case 'episode-care':
        navigate(`${patients}/${patientDetails}${episodeOfCare}`, {
          state: { patientId: getPatientId },
        });
        break;
      case 'mds':
        navigate(`${patients}/${patientDetails}${mdsCommunication}`, {
          state: { patientId: getPatientId, patientData },
        });
        break;
      default:
        break;
    }
  };

  const getTabsValue = () => {
    switch (location.pathname) {
      case `${patients}/${patientDetails}`:
        return 'details';
      case `${patients}/${patientDetails}${screening}`:
        return 'screening';
      case `${patients}/${patientDetails}${episodeOfCare}`:
        return 'episode-care';
      case `${patients}/${patientDetails}${mdsCommunication}`:
      case `${patients}/${patientDetails}${mdsCommunication}${evalMds}`:
      case `${patients}/${patientDetails}${mdsCommunication}${recertiMds}`:
      case `${patients}/${patientDetails}${mdsCommunication}${dischargeMds}`:
        return 'mds';
      default:
        return '';
    }
  };

  const handleBackPatient = () => {
    dispatch(persistPatientIdAction(getPatientId));
  };

  return (
    <>
      <Seo title="Dashboard: Customer Details" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 8,
        }}
      >
        <Container maxWidth={'xl'}>
          <Stack spacing={4}>
            <Stack spacing={4}>
              <div>
                <Link
                  color="text.primary"
                  component={RouterLink}
                  href={paths.patients}
                  sx={{
                    alignItems: 'center',
                    display: 'inline-flex',
                  }}
                  underline="hover"
                  onClick={handleBackPatient}
                >
                  <SvgIcon sx={{ mr: 1 }}>
                    <ArrowLeftIcon />
                  </SvgIcon>
                  <Typography variant="subtitle2">Back to Patient List</Typography>
                </Link>
              </div>
              <Stack
                alignItems="flex-start"
                direction={'row'}
                justifyContent="space-between"
                spacing={4}
              >
                <Stack
                  alignItems="center"
                  direction="row"
                  spacing={2}
                >
                  {patientDataLoader?.loading ? (
                    <Stack>
                      <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', gap: 1 }}>
                        <Skeleton
                          animation="pulse"
                          variant="circular"
                        >
                          <Avatar sx={{ width: 70, height: 70 }} />
                        </Skeleton>
                        <Box sx={{ width: 100 }}>
                          <Typography
                            width={'100%'}
                            variant="h5"
                          >
                            <Skeleton animation="pulse" />
                          </Typography>
                          <Typography
                            width={'30%'}
                            variant="h5"
                          >
                            <Skeleton animation="pulse" />
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  ) : (
                    <>
                      <Avatar
                        src={`data:image/png;base64,${patientData?.profileImage}`}
                        sx={{
                          height: 64,
                          width: 64,
                        }}
                      >
                        {getInitials(patientData?.lastName) + getInitials(patientData?.firstName)}
                      </Avatar>
                      <Stack spacing={1}>
                        <Typography variant="h4">
                          {patientData?.lastName ? patientData?.lastName + ' ' : ''}
                          {patientData?.firstName ? patientData?.firstName + ' ' : ''}
                        </Typography>
                        <Stack
                          alignItems="center"
                          direction="row"
                          spacing={1}
                        >
                          <Chip
                            label={patientData?.active === true ? 'Active' : 'Inactive'}
                            size="small"
                            color={patientData?.active === true ? 'success' : 'warning'}
                            clickable={true}
                          />
                        </Stack>
                      </Stack>
                    </>
                  )}
                </Stack>
                <Stack
                  alignItems="center"
                  direction="row"
                  spacing={2}
                >
                  <Stack>
                    <Button
                      onClick={handleOpenPatientInfoDrawer}
                      variant="outlined"
                    >
                      Patient Details
                    </Button>
                  </Stack>
                  {((patientData?.active && permissionData?.includes(DEACTIVATE_PATIENT)) ||
                    (!patientData?.active && permissionData?.includes(ACTIVATE_PATIENT))) && (
                    <Stack
                      alignItems="center"
                      direction="row"
                      spacing={2}
                    >
                      <Button
                        color="error"
                        variant="outlined"
                        onClick={onDeactivatePatient}
                        disabled={
                          patientData?.active
                            ? !permissionData?.includes(DEACTIVATE_PATIENT)
                            : !permissionData?.includes(ACTIVATE_PATIENT)
                        }
                      >
                        <img
                          style={{ marginRight: '.5rem' }}
                          src={deactiveIcon}
                          alt=""
                        />{' '}
                        {patientData?.active ? 'Deactivate' : 'Activate'}
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Stack>
              <div>
                <Tabs
                  indicatorColor="primary"
                  scrollButtons="auto"
                  sx={{ mt: 3 }}
                  textColor="primary"
                  onChange={(event, value) => redirectToTab(value)}
                  variant="scrollable"
                  value={getTabsValue()}
                >
                  {tabs
                    ?.filter((tab) => {
                      if (
                        tab?.value === 'episode-care' &&
                        permissionData?.includes(READ_EPISODE_OF_CARE)
                      )
                        return true;
                      if (tab?.value === 'screening' && permissionData?.includes(READ_SCREENINGS))
                        return true;
                      if (tab?.value === 'details') return true;
                      if (tab?.value === 'mds' && isMdsOn) return true;
                      return false;
                    })
                    .map((tab: any) => {
                      return (
                        <Tab
                          key={tab.value}
                          label={tab.label}
                          value={tab.value}
                        />
                      );
                    })}
                </Tabs>
                <Divider />
              </div>
            </Stack>
            <Outlet />
            {location.pathname === `${patients}/${patientDetails}` && (
              <div>
                <PatientInfo patientId={getPatientId} />
              </div>
            )}
          </Stack>
        </Container>
        <Drawer
          anchor="right"
          onClose={handleClosePatientInfoDrawer}
          open={openPatientInfoDetails}
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: 444,
            },
          }}
        >
          <PatientInfoDrawer
            patientId={getPatientId}
            handleClosePatientInfoDrawer={handleClosePatientInfoDrawer}
          />
        </Drawer>
      </Box>
    </>
  );
};

export default PatientDetails;
